// @flow
import React from 'react'
import { connect } from 'react-redux'
import { Dialog } from 'components/Dialog'
import {
  archiveFile,
  downloadFile,
  fetchDigitalFile,
  moveFile,
  previewFile,
  renameFile,
  restoreFile,
  selectPath as selectPathAction,
  uploadFile,
} from 'lib/digital_file/actions'
import {
  getArchivedAtSelectedPath,
  getCountsByPath,
  getFilesAtSelectedPath,
  getLegacy,
  getSelectedPath,
  getTemplate,
  isUploading,
} from 'lib/digital_file/selectors'
import MoveDialog from './MoveDialog'
import RenameDialog from './RenameDialog'
import Template from './Template'

type Props = {
  accountNumber: string,
  bordered?: boolean,
  template: Array<Object>,
  countsByPath: { [path: string]: number },
  selectedPath: ?string,
  files: Array<Object>,
  legacy: Array<Object>,
  archived: Array<Object>,
  uploading: boolean,
  dispatch: Function,
}

type State = {
  archiveTarget: ?number,
  moveTarget: ?number,
  renameTarget: ?number,
}

class DigitalFile extends React.Component<Props, State> {
  state = { archiveTarget: null, moveTarget: null, renameTarget: null }

  componentDidMount() {
    this.props.dispatch(fetchDigitalFile(this.props.accountNumber))
  }

  componentDidUpdate(prev: Props) {
    if (prev.accountNumber !== this.props.accountNumber) {
      this.props.dispatch(fetchDigitalFile(this.props.accountNumber))
    }
  }

  handleSelectPath = (path: string) => {
    this.props.dispatch(selectPathAction(this.props.accountNumber, path))
  }

  handleUpload = (params: Object) => {
    return this.props.dispatch(uploadFile(this.props.accountNumber, params))
  }

  handleArchive = (id: number) => {
    this.setState({ archiveTarget: id })
  }

  _confirmArchive = (id: number) => {
    this.props.dispatch(archiveFile(this.props.accountNumber, id))
    this.setState({ archiveTarget: null })
  }

  _cancelArchive = () => {
    this.setState({ archiveTarget: null })
  }

  handleRestore = (id: number) => {
    this.props.dispatch(restoreFile(this.props.accountNumber, id))
  }

  handleView = (key: ?string, contentType: ?string, name: ?string) => {
    this.props.dispatch(previewFile(key, contentType, name))
  }

  handleDownload = (key: ?string) => {
    this.props.dispatch(downloadFile(key))
  }

  handleMove = (id: number) => {
    this.setState({ moveTarget: id })
  }

  _confirmMove = (path: string) => {
    const id = this.state.moveTarget
    if (id == null) return Promise.resolve()
    return this.props
      .dispatch(moveFile(this.props.accountNumber, id, path))
      .then(() => {
        this.setState({ moveTarget: null })
      })
  }

  _cancelMove = () => {
    this.setState({ moveTarget: null })
  }

  _archiveFromMove = () => {
    const id = this.state.moveTarget
    if (id == null) return
    this.props.dispatch(archiveFile(this.props.accountNumber, id))
    this.setState({ moveTarget: null })
  }

  handleRename = (id: number) => {
    this.setState({ renameTarget: id })
  }

  _confirmRename = (name: string) => {
    const id = this.state.renameTarget
    if (id == null) return Promise.resolve()
    return this.props
      .dispatch(renameFile(this.props.accountNumber, id, name))
      .then(() => {
        this.setState({ renameTarget: null })
      })
  }

  _cancelRename = () => {
    this.setState({ renameTarget: null })
  }

  _archiveHeaderText = (id: number) => {
    const file = this.props.files.find((f) => f.id === id)
    return file
      ? `Are you sure you want to archive "${file.name}"?`
      : 'Are you sure you want to archive this file?'
  }

  render() {
    const {
      bordered = true,
      template,
      countsByPath,
      selectedPath,
      files,
      legacy,
      archived,
      uploading,
    } = this.props
    const { archiveTarget, moveTarget, renameTarget } = this.state
    const moveFileObj =
      moveTarget != null
        ? files.find((f) => f.id === moveTarget) ||
          legacy.find((f) => f.id === moveTarget)
        : null
    const renameFileObj =
      renameTarget != null
        ? files.find((f) => f.id === renameTarget) ||
          legacy.find((f) => f.id === renameTarget)
        : null
    return (
      <>
        <Template
          bordered={bordered}
          template={template}
          countsByPath={countsByPath}
          selectedPath={selectedPath}
          files={files}
          legacy={legacy}
          archived={archived}
          uploading={uploading}
          onSelectPath={this.handleSelectPath}
          onUpload={this.handleUpload}
          onArchive={this.handleArchive}
          onRestore={this.handleRestore}
          onView={this.handleView}
          onDownload={this.handleDownload}
          onMove={this.handleMove}
          onRename={this.handleRename}
        />
        {archiveTarget != null && (
          <Dialog
            cancelButtonText="Cancel"
            confirmButtonText="Yes, Archive"
            headerText={this._archiveHeaderText(archiveTarget)}
            onCancel={this._cancelArchive}
            onConfirm={() => this._confirmArchive(archiveTarget)}
          />
        )}
        {moveFileObj && (
          <MoveDialog
            file={moveFileObj}
            template={template}
            onCancel={this._cancelMove}
            onConfirm={this._confirmMove}
            onArchive={this._archiveFromMove}
          />
        )}
        {renameFileObj && (
          <RenameDialog
            file={renameFileObj}
            onCancel={this._cancelRename}
            onConfirm={this._confirmRename}
          />
        )}
      </>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  template: getTemplate(state, ownProps.accountNumber),
  countsByPath: getCountsByPath(state, ownProps.accountNumber),
  selectedPath: getSelectedPath(state, ownProps.accountNumber),
  files: getFilesAtSelectedPath(state, ownProps.accountNumber),
  legacy: getLegacy(state, ownProps.accountNumber),
  archived: getArchivedAtSelectedPath(state, ownProps.accountNumber),
  uploading: isUploading(state, ownProps.accountNumber),
})

export default connect(mapStateToProps)(DigitalFile)
